import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, ReactNode } from 'react';
import { ApiError, requestJson, toApiError } from '../api/http';
import {
  deleteVoiceApi,
  fetchVoiceStatusApi,
  fetchVoicesApi,
  retrainVoiceApi,
  trainVoiceApi,
  updateVoiceNicknameApi,
  uploadVoiceSamplesApi,
} from '../api/voicesApi';

import { pruneMissingOfflineAudio } from '../api/offlineCache';
import { clearPrivateOfflineData, isCurrentSession, readOfflineSnapshot, saveOfflineSnapshot, sessionIdentity } from '../api/offlineSession';

// Types
export interface Story {
  content_scope?: import('../api/contentScope').ContentScope | null;
  story_id: string;
  title: string;
  description: string;
  age_range: string;
  tags: string[];
  content?: string;
  thumbnail_url?: string | null;
  thumbnail_srcset?: string | null;
  summary?: string;
  book_id?: string | null;
  book_summary?: string | null;
  book_thumbnail_url?: string | null;
  book_thumbnail_srcset?: string | null;
  book_title?: string | null;
  chapter_number?: number | null;
  chapter_title?: string | null;
  theme?: string | null;
  estimated_duration_minutes?: number | null;
  collection_id?: string | null;
  content_kind?: string | null;
}

export interface StoryFilters {
  search?: string;
  age?: string;
  theme?: string;
  tags?: string[];
  collection?: string;
  length?: 'up_to_5' | '6_to_10' | '11_to_15' | '16_plus';
  minDurationMinutes?: number;
  maxDurationMinutes?: number;
  offset?: number;
  limit?: number;
}

export interface Voice {
  voice_id: string;
  voice_name: string;
  status: 'processing' | 'training' | 'ready' | 'failed';
  household_id?: string | null;
  consent_active?: boolean | null;
  sample_count?: number | null;
  has_cartesia?: boolean;
  profile_picture_url?: string | null;
}

export interface User {
  id: string;
  email: string;
  full_name?: string;
  username?: string;
  name?: string;  // Google OAuth name
  picture?: string;  // Google OAuth profile picture
  is_premium: boolean;
  is_internal_operator?: boolean;
  isAuthenticated: boolean;
}

interface AppState {
  stories: Story[];
  voices: Voice[];
  isLoading: boolean;
  error: string | null;
  currentStory: Story | null;
  currentVoice: Voice | null;
  user: User | null;
  isAuthenticated: boolean;
  authLoading: boolean;
  offlineSession?: boolean;
}

type AppAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_STORIES'; payload: Story[] }
  | { type: 'SET_VOICES'; payload: Voice[] }
  | { type: 'SET_CURRENT_STORY'; payload: Story | null }
  | { type: 'SET_CURRENT_VOICE'; payload: Voice | null }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_AUTH_LOADING'; payload: boolean }
  | { type: 'SET_OFFLINE_SESSION'; payload: boolean }
  | { type: 'LOGOUT' };

const initialState: AppState = {
  stories: [],
  voices: [],
  isLoading: false,
  error: null,
  currentStory: null,
  currentVoice: null,
  user: null,
  isAuthenticated: false,
  authLoading: true,
};

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SET_OFFLINE_SESSION':
      return { ...state, offlineSession: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_STORIES':
      return { ...state, stories: action.payload };
    case 'SET_VOICES':
      return { ...state, voices: action.payload };
    case 'SET_CURRENT_STORY':
      return { ...state, currentStory: action.payload };
    case 'SET_CURRENT_VOICE':
      return { ...state, currentVoice: action.payload };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: action.payload !== null,
        authLoading: false
      };
    case 'SET_AUTH_LOADING':
      return {
        ...state,
        authLoading: action.payload
      };
    case 'LOGOUT':
      return {
        ...initialState,
        user: null,
        isAuthenticated: false,
        authLoading: false
      };
    default:
      return state;
  }
}

interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  fetchStories: (filters?: StoryFilters) => Promise<void>;
  fetchVoices: (throwOnError?: boolean) => Promise<void>;
  uploadVoiceSamples: (voiceId: string, files: File[], nickname?: string) => Promise<string | null>;
  trainVoice: (voiceId: string) => Promise<boolean>;
  checkVoiceStatus: (voiceId: string) => Promise<Voice | null>;
  deleteVoice: (voiceId: string) => Promise<boolean>;
  retrainVoice: (voiceId: string) => Promise<boolean>;
  updateVoiceNickname: (voiceId: string, newNickname: string) => Promise<Voice | null>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Export AppContext for use in components
export { AppContext };

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:8000';

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const storyRequestId = useRef(0);

  // A failed connection is not a rejected credential. Only an explicit
  // rejection clears the sign-in; a bounded, token-bound snapshot can reopen.
  useEffect(() => {
    let generation = 0;
    let stopped = false;
    let reconnectTimer: number | undefined;
    let retryDelay = 5000;
    let usingSavedSession = false;
    const initializeAuth = async () => {
      window.clearTimeout(reconnectTimer);
      const attempt = ++generation;
      const identity = sessionIdentity();
      if (!identity) { dispatch({ type: 'SET_USER', payload: null }); return; }
      const current = () => !stopped && attempt === generation && isCurrentSession(identity);
      await pruneMissingOfflineAudio(identity.userId);
      if (!current()) return;
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 3000);
      try {
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${identity.token}`, 'Content-Type': 'application/json' },
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!current()) return;
        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_id');
            if (!stopped && attempt === generation) dispatch({ type: 'LOGOUT' });
            await clearPrivateOfflineData();
            return;
          }
          throw new Error('Sign-in service unavailable');
        }
        const data = await response.json();
        if (!current()) return;
        if (data.id !== identity.userId || typeof data.email !== 'string') {
          localStorage.removeItem('auth_token'); localStorage.removeItem('user_id');
          if (!stopped && attempt === generation) dispatch({ type: 'LOGOUT' });
          await clearPrivateOfflineData();
          return;
        }
        const user: User = {
          id: data.id, email: data.email, name: data.full_name || data.email,
          full_name: data.full_name, username: data.username, is_premium: data.is_premium,
          is_internal_operator: data.is_internal_operator, picture: data.profile_picture_url, isAuthenticated: true,
        };
        await saveOfflineSnapshot('user', user, identity);
        if (!current()) return;
        usingSavedSession = false; retryDelay = 5000;
        dispatch({ type: 'SET_OFFLINE_SESSION', payload: false });
        dispatch({ type: 'SET_USER', payload: user });
      } catch {
        const saved = await readOfflineSnapshot<User>('user', identity);
        if (!current()) return;
        const user = saved?.id === identity.userId && typeof saved.email === 'string'
          ? { ...saved, is_internal_operator: false } : null;
        dispatch({ type: 'SET_OFFLINE_SESSION', payload: Boolean(user) });
        dispatch({ type: 'SET_USER', payload: user });
        usingSavedSession = Boolean(user);
        if (user) {
          reconnectTimer = window.setTimeout(initializeAuth, retryDelay);
          retryDelay = Math.min(retryDelay * 2, 60000);
        }
      } finally { window.clearTimeout(timer); }
    };
    void initializeAuth();
    // Connectivity events can arrive before the browser’s transport is ready.
    const onOnline = () => { window.clearTimeout(reconnectTimer); reconnectTimer = window.setTimeout(initializeAuth, 350); };
    window.addEventListener('online', onOnline);
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'auth_token' || event.key === 'user_id') {
        dispatch({ type: 'LOGOUT' });
        void initializeAuth();
      }
    };
    // Some browsers report online throughout a network outage and emit no event.
    const onVisible = () => { if (usingSavedSession && document.visibilityState === 'visible') onOnline(); };
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storage', onStorage);
    return () => { stopped = true; window.clearTimeout(reconnectTimer); window.removeEventListener('online', onOnline); window.removeEventListener('storage', onStorage); window.removeEventListener('focus', onVisible); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  const fetchStories = useCallback(async (filters: StoryFilters = {}) => {
    const requestId = ++storyRequestId.current;
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.age) params.set('age', filters.age);
      if (filters.theme) params.set('theme', filters.theme);
      if (filters.tags?.length) params.set('tags', filters.tags.join(','));
      if (filters.collection) params.set('collection', filters.collection);
      if (filters.length) params.set('length', filters.length);
      if (filters.minDurationMinutes !== undefined) {
        params.set('min_duration_minutes', String(filters.minDurationMinutes));
      }
      if (filters.maxDurationMinutes !== undefined) {
        params.set('max_duration_minutes', String(filters.maxDurationMinutes));
      }
      if (filters.offset !== undefined) params.set('offset', String(filters.offset));
      if (filters.limit !== undefined) params.set('limit', String(filters.limit));
      const query = params.toString();
      const stories = await requestJson<Story[]>(`${API_BASE_URL}/stories/${query ? `?${query}` : ''}`);
      if (requestId === storyRequestId.current) {
        dispatch({ type: 'SET_STORIES', payload: stories });
      }
    } catch (error) {
      if (requestId === storyRequestId.current) {
        dispatch({ type: 'SET_ERROR', payload: toApiError(error).code });
      }
    } finally {
      if (requestId === storyRequestId.current) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    }
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated || !navigator.serviceWorker
      || new URL(API_BASE_URL, window.location.href).origin !== window.location.origin) return;
    // The first catalog fetch can beat initial worker activation. Repeat it
    // under worker control so the first online visit is enough to cache it.
    const onController = () => { void fetchStories(); };
    navigator.serviceWorker.addEventListener('controllerchange', onController);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onController);
  }, [fetchStories, state.isAuthenticated]);

  const fetchVoices = useCallback(async (throwOnError = false) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const voices = await fetchVoicesApi();
      dispatch({ type: 'SET_VOICES', payload: voices });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: toApiError(error).code });
      if (throwOnError) throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  const uploadVoiceSamples = useCallback(async (voiceId: string, files: File[], nickname?: string): Promise<string | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const actualVoiceId = await uploadVoiceSamplesApi(voiceId, files, nickname);
      // Refresh voices after upload
      await fetchVoices();
      
      return actualVoiceId;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: toApiError(error).code });
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [fetchVoices]);

  const trainVoice = useCallback(async (voiceId: string): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      await trainVoiceApi(voiceId);
      // Refresh voices after training starts
      await fetchVoices();
      
      return true;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: toApiError(error).code });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [fetchVoices]);

  const checkVoiceStatus = useCallback(async (voiceId: string): Promise<Voice | null> => {
    try {
      const status = await fetchVoiceStatusApi(voiceId);
      const current = state.voices.find((voice) => voice.voice_id === voiceId);
      const voice: Voice = current
        ? { ...current, status: status.status }
        : { voice_id: status.voice_id, voice_name: 'Family voice', status: status.status };
      
      // Update the voice in the state
      const updatedVoices = state.voices.map(v => 
        v.voice_id === voiceId ? voice : v
      );
      dispatch({ type: 'SET_VOICES', payload: updatedVoices });
      
      return voice;
    } catch (error) {
      if (error instanceof ApiError && error.code === 'not_found') return null;
      return null;
    }
  }, [state.voices]);

  const deleteVoice = useCallback(async (voiceId: string): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      await deleteVoiceApi(voiceId);
      // Refresh voices after deletion
      await fetchVoices();
      
      return true;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: toApiError(error).code });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [fetchVoices]);

  const retrainVoice = useCallback(async (voiceId: string): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      await retrainVoiceApi(voiceId);
      // Refresh voices after retraining
      await fetchVoices();
      
      return true;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: toApiError(error).code });
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [fetchVoices]);

  const updateVoiceNickname = useCallback(async (voiceId: string, newNickname: string): Promise<Voice | null> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      const updatedVoice = await updateVoiceNicknameApi(voiceId, newNickname);
      
      // Update the voice in the state
      dispatch({
        type: 'SET_VOICES',
        payload: state.voices.map(voice => 
          voice.voice_id === voiceId ? { ...voice, voice_name: newNickname } : voice
        ),
      });
      
      // If this is the current voice, update it too
      if (state.currentVoice && state.currentVoice.voice_id === voiceId) {
        dispatch({
          type: 'SET_CURRENT_VOICE',
          payload: { ...state.currentVoice, voice_name: newNickname },
        });
      }
      
      return updatedVoice;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: toApiError(error).code });
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [state.voices, state.currentVoice]);

  const value: AppContextType = {
    state,
    dispatch,
    fetchStories,
    fetchVoices,
    uploadVoiceSamples,
    trainVoice,
    checkVoiceStatus,
    deleteVoice,
    retrainVoice,
    updateVoiceNickname,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
